import {
  DeleteOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Banner,
  Button,
  Heading,
  Input,
  KeywordsBroadMatchTable,
  Switch,
  Tooltip,
} from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { SMSKeywordsSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useChatStartersCRUDActions } from '@mm/state/actions/ChatStartersCRUDActinos';
import { useTriggerCRUDActions } from '@mm/state/actions/TriggerCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import TriggerModel from '@mm/state/model/TriggerModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { words } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { useDeleteAutomationTrigger } from '../../../../features/beta/modules/Automations/AutomationEdit/InputTriggers/use-delete-automation-trigger';
import { SmsIphonePreview } from '../../../../features/campaigns/components/SmsIphonePreview/SmsIphonePreview';
import {
  BROAD_MATCH,
  BROAD_MATCH_LABELS,
} from '../../../../features/startBotPromoters/commentGuards/components/EditFBPost/KeywordsForm/BroadMatch';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './SMSKeywordSlideoutEditor.module.css';

interface Props {
  triggerId?: number;
  campaignId?: number;
  audienceId?: number;
}

export const SMSKeywordSlideoutEditor = ({ triggerId }: Props) => {
  const bot = useSelector(activeChatbotSelector);
  const botId = bot?.id;

  const triggerCRUDActions = useTriggerCRUDActions();
  const chatStartersCRUDActions = useChatStartersCRUDActions();
  const [originalTrigger, setOriginalTrigger] = useState<TriggerModel>();
  const [newKeyword, setNewKeyword] = useState('');
  const updatingTrigger = useSelector(
    (state: StoreState) => state.trigger.busy,
  );
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;
  const [shouldEditTrigger, setShouldEditTrigger] = useState(false);
  const { setRef } = useSlideoutOnClose();

  const isDefaultTrigger = (trigger: TriggerModel | undefined) => {
    if (!trigger) {
      return false;
    }
    return Boolean(
      trigger.phrases.find(
        trigger => trigger.phrase.toLocaleLowerCase() === 'keyword',
      ),
    );
  };

  const chatStarterCRUDActions = useChatStartersCRUDActions();

  const { automationId, handleDelete } = useDeleteAutomationTrigger();

  useEffect(() => {
    async function fetchTriggers() {
      if (!triggerId) {
        return;
      }
      const trigger = await triggerCRUDActions.retrieveById(triggerId);
      setOriginalTrigger(trigger);
      setShouldEditTrigger(isDefaultTrigger(trigger));
      setNewKeyword(trigger?.phrases[0]?.phrase ?? '');

      await chatStarterCRUDActions.retrieveAll({
        botId,
        kind: 'sms_keywords',
        pagination: { page: 1, perPage: 500 },
      });
    }
    fetchTriggers();
  }, [triggerCRUDActions, triggerId]);

  const dialogueId = originalTrigger?.botResponses[0]?.pageId;
  const keywordChanged = originalTrigger?.phrases[0]?.phrase !== newKeyword;
  const keywordInvalid = !newKeyword.trim().length;
  const modalActions = useModalActions();
  const viewActions = useViewActions();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const onSaveKeyword = async () => {
    const updatedTrigger = await triggerCRUDActions.update({
      ...originalTrigger,
      phrases: [{ phrase: newKeyword, sortOrder: 1 }],
    });
    setOriginalTrigger(updatedTrigger);
    setShouldEditTrigger(isDefaultTrigger(updatedTrigger));

    await chatStarterCRUDActions.retrieveAll({
      botId,
      kind: 'sms_keywords',
      pagination: { page: 1, perPage: 500 },
    });
  };

  const onDelete = async () => {
    if (originalTrigger) {
      if (!automationId) {
        modalActions.openDeleteConfirmModal({
          headerText: 'Are you sure you want to delete this keyword trigger?',
          deleteCallback: async () => {
            await triggerCRUDActions.delete(originalTrigger);
            await chatStartersCRUDActions.retrieveAll({
              botId: chatbotId,
              kind: 'sms_keywords',
            });
            viewActions.hideSlideoutMenu();
          },
        });
      }

      if (automationId) {
        await handleDelete('trigger', originalTrigger.id, 'keyword trigger');
        viewActions.hideSlideoutMenu();
      }
    }
  };

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <SlideoutMenu.Header className={styles.header} closeIcon="close">
        <Heading size={3} className={styles.heading}>
          SMS Keywords
        </Heading>

        <Switch
          checked={originalTrigger?.active || false}
          disabled={true}
          label={originalTrigger?.active ? 'active' : 'inactive'}
          css={{
            container: styles.switch,
            thumb: originalTrigger?.active
              ? styles.thumbActive
              : styles.thumbInactive,
            label: originalTrigger?.active
              ? styles.switchLabelActive
              : styles.switchLabelInactive,
          }}
        />

        {triggerId ? (
          <div className={styles.deleteContainer} onClick={onDelete}>
            <DeleteOutlined />
          </div>
        ) : null}
      </SlideoutMenu.Header>

      <FreeTrialWarning />

      <SlideoutMenu.Content className={styles.content}>
        {!isMobile && (
          <Banner
            css={{
              container: styles.banner,
              header: styles.bannerHeader,
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
              closeIcon: styles.bannerCloseIcon,
            }}
            withBannerHeader
            showCloseIcon
            usedInSlideout
            withLeftArea={false}
            headerText="What are SMS Keywords?"
          >
            <SMSKeywordsSlideoutBanner textColor="white" />
          </Banner>
        )}
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              orderNumber="1"
              containerName="Enter Keyword that will trigger this automation when a contact sends it to start a conversation"
              className={styles.collapsibleContainer}
              stepperStylingPlatform="sms"
              useCircleNumberStyle
            >
              {shouldEditTrigger && (
                <div className={styles.warning}>
                  <ExclamationCircleFilled className={styles.warningIcon} />
                  Please update the keyword below{' '}
                </div>
              )}
              <div className={styles.keywordInputRow}>
                <form
                  className={styles.keywordInputArea}
                  onSubmit={e => {
                    e.preventDefault();
                    onSaveKeyword();
                  }}
                >
                  <Input
                    type="text"
                    placeholder="e.g. “buy a home”,"
                    value={newKeyword}
                    onChange={e =>
                      setNewKeyword(
                        words(e.target.value).join('').toUpperCase(),
                      )
                    }
                    disabled={updatingTrigger}
                    css={{
                      container: styles.keywordsContainer,
                      input: styles.keywordsInput,
                    }}
                  />{' '}
                  {keywordChanged && (
                    <Button
                      type="submit"
                      color="blue"
                      disabled={updatingTrigger || keywordInvalid}
                      css={{ container: styles.addButton }}
                    >
                      Update
                    </Button>
                  )}
                </form>
                <div className={styles.underInputLabelContainer}>
                  <div className={styles.exactMatchLabel}>
                    SMS Keywords are Exact Match
                  </div>
                  <Tooltip
                    isWhite
                    title={
                      <KeywordsBroadMatchTable
                        isMM
                        broadMatch={BROAD_MATCH}
                        labels={BROAD_MATCH_LABELS}
                      />
                    }
                    placement="bottom"
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
              </div>
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="2"
              containerName="Reply instantly to users who send the keywords "
              className={styles.collapsibleContainer}
              stepperStylingPlatform="sms"
              useCircleNumberStyle
            >
              <EmbeddableDialogueEditorWithPreview
                className={styles.dialogueEditor}
                dialogueId={dialogueId}
                platforms={['sms']}
                usedInSlideout
                hideDialogueSelector
              />
            </CollapsibleContentContainer>
          </div>
          {!isMobile && (
            <SmsIphonePreview
              destinationPageId={dialogueId}
              testNumber={false}
              callForwarding
              className={styles.smsPreview}
            />
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
